/**
 * Payments tab.
 *
 * Parity target: Angular `paymentTemplate` (config.component.html
 * lines 1034–1538). Covers:
 *   - 4-gateway matrix (PAYPAL / CARDCONNECT / PAYNEARME / UPTEMPO)
 *     each with on/off, fees, display order, and YES/NO flags for
 *     use_for_app / allow_invoice / allow_refund
 *   - CardConnect gateway credentials (user/pass/merchant/endpoint +
 *     daily capture time)
 *   - Default ACH + UPTEMPO program switches
 *   - Payment Window (minutes)
 *
 * Deferred: MRG Percent read-only table (per-year history,
 * `getFlattenedMrgData` — not critical for day-to-day ops).
 */

import { useEffect, useState } from 'react'
import { useForm, type Path } from 'react-hook-form'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { showApiError, showError, showSuccess } from '@/lib/utils/notifications'
import {
  buildPaymentPayload,
  formatMrgDate,
  saveConfig,
} from '../../services/configService'
import { validateYardiDataFetchRangeByDays } from '../../utils/yardiFetchRangeValidation'
import type {
  OnOff,
  PaymentFormValues,
  PaymentGatewayFormValues,
} from '../../types/config'
import {
  decimalTextInputAttrs,
  FieldLegend,
  integerTextInputAttrs,
  LabeledInput,
  MaskedInput,
  SegmentedButtonGroup,
  } from './formPrimitives'
import { cn } from '@/lib/utils/helpers'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import {
  ListingDataTableCard,
  ListingDataTableScroll,
  LISTING_DATA_TABLE_BODY_CLASS,
  LISTING_DATA_TABLE_CLASS,
  LISTING_DATA_TABLE_HEAD_CLASS,
} from '@/components/ui/listingDataTable'
import {
  validateCaptureTime,
  validatePaymentWindow,
} from '../../utils/paymentFieldValidation'
import { SectionCard } from '@/components/ui/SectionCard'

import type { PaymentsTabProps } from '../../types'

const EMPTY_GATEWAY: PaymentGatewayFormValues = {
  payment_status: '',
  certificate_surcharge: '',
  service_fee: '',
  display_order: '',
  use_for_app: false,
  allow_invoice: false,
  allow_refund: false,
  refund_fee: '',
}

const EMPTY: PaymentFormValues = {
  PAYPAL: { ...EMPTY_GATEWAY },
  CARDCONNECT: { ...EMPTY_GATEWAY },
  PAYNEARME: { ...EMPTY_GATEWAY },
  UPTEMPO: { ...EMPTY_GATEWAY },
  CARDCONNECT_USERNAME: '',
  CARDCONNECT_PASSWORD: '',
  CARDCONNECT_MERCHANT_ID: '',
  CARDCONNECT_ENDPOINT: '',
  CARDCONNECT_CAPTURE_TIME: '',
  DEFAULT_ACH_STATUS: '',
  UPTEMPO_PROGRAM_STATUS: '',
  PAYMENT_WINDOW: '',
}

const GATEWAYS: {
  key: 'PAYPAL' | 'CARDCONNECT' | 'PAYNEARME' | 'UPTEMPO'
  label: string
}[] = [
  { key: 'PAYPAL', label: 'Paypal' },
  { key: 'CARDCONNECT', label: 'CardConnect' },
  { key: 'PAYNEARME', label: 'PayNearMe' },
  { key: 'UPTEMPO', label: 'UpTempo (ACH)' },
]

export function PaymentsTab({
  initialValues,
  mrgRows,
  isLoading,
  yardiDataFetchRangeByDays,
  onSaved,
}: PaymentsTabProps) {
  const [submitLoading, setSubmitLoading] = useState(false)
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { isDirty, isValid, errors },
  } = useForm<PaymentFormValues>({ defaultValues: EMPTY, mode: 'onChange' })

  useEffect(() => {
    if (!initialValues) return
    reset(initialValues)
    // Nested gateway fields can leave `isValid` stale until a validation pass (Submit stayed disabled — QA #97).
    void trigger()
  }, [initialValues, reset, trigger])

  const onSubmit = async (values: PaymentFormValues) => {
    const rangeCheck = validateYardiDataFetchRangeByDays(yardiDataFetchRangeByDays)
    if (rangeCheck !== true) {
      showError(rangeCheck)
      return
    }
    try {
      setSubmitLoading(true)
      await saveConfig(buildPaymentPayload(values))
      showSuccess('Config Change Successfully!')
      reset(values)
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  if (isLoading) {
    return <ModuleScreenLoading minHeight="300px" />
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <SectionCard number={1} title="Payment Gateway">
        <ListingDataTableCard>
          <ListingDataTableScroll>
          <table className={LISTING_DATA_TABLE_CLASS}>
            <thead className={cn(LISTING_DATA_TABLE_HEAD_CLASS, 'text-xs')}>
              <tr>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Certificate Surcharge</th>
                <th className="px-3 py-2 text-left">Service Fee</th>
                <th className="px-3 py-2 text-left">Display Order</th>
                <th className="px-3 py-2 text-center">Use for App?</th>
                <th className="px-3 py-2 text-center">Allow Invoice?</th>
                <th className="px-3 py-2 text-center">Allow Refund?</th>
                <th className="px-3 py-2 text-left">Refund Fee (%)</th>
              </tr>
            </thead>
            <tbody className={LISTING_DATA_TABLE_BODY_CLASS}>
              {GATEWAYS.map(({ key, label }) => {
                const fieldPath = (suffix: string) =>
                  `${key}.${suffix}` as Path<PaymentFormValues>
                const paymentStatus = watch(fieldPath('payment_status')) as OnOff | undefined
                return (
                  <tr key={key}>
                    <td className="px-3 py-2 font-medium text-slate-900">{label}</td>
                    <td className="px-3 py-2">
                      <SegmentedButtonGroup
                        ariaLabel={`${label} payment status`}
                        value={paymentStatus ?? ''}
                        onChange={(v) =>
                          setValue(fieldPath('payment_status'), v as OnOff, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        options={[
                          { value: 'ON', label: 'On' },
                          { value: 'OFF', label: 'Off' },
                        ]}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...decimalTextInputAttrs}
                        {...register(fieldPath('certificate_surcharge'))}
                        className="w-24 px-2 py-1 text-sm border border-slate-300 rounded"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...decimalTextInputAttrs}
                        {...register(fieldPath('service_fee'))}
                        className="w-24 px-2 py-1 text-sm border border-slate-300 rounded"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...decimalTextInputAttrs}
                        {...register(fieldPath('display_order'))}
                        className="w-20 px-2 py-1 text-sm border border-slate-300 rounded"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        {...register(fieldPath('use_for_app'))}
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        {...register(fieldPath('allow_invoice'))}
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        {...register(fieldPath('allow_refund'))}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...decimalTextInputAttrs}
                        {...register(fieldPath('refund_fee'))}
                        className="w-20 px-2 py-1 text-sm border border-slate-300 rounded"
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </ListingDataTableScroll>
        </ListingDataTableCard>
      </SectionCard>

      <SectionCard number={2} title="Payment Gateway Config — CardConnect">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
          <LabeledInput label="Username" {...register('CARDCONNECT_USERNAME')} />
          <MaskedInput label="Password" {...register('CARDCONNECT_PASSWORD')} />
          <LabeledInput label="Merchant ID" {...register('CARDCONNECT_MERCHANT_ID')} />
          <LabeledInput
            label="Endpoint"
            className="sm:col-span-2"
            {...register('CARDCONNECT_ENDPOINT')}
          />
          <div>
            <LabeledInput
              label="Daily Capture Time (CST)"
              type="time"
              {...register('CARDCONNECT_CAPTURE_TIME', {
                validate: validateCaptureTime,
              })}
            />
            {errors.CARDCONNECT_CAPTURE_TIME?.message && (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {errors.CARDCONNECT_CAPTURE_TIME.message}
              </p>
            )}
          </div>
        </div>
      </SectionCard>

      <SectionCard number={3} title="MRG Percent">
        {mrgRows.length === 0 ? (
          <p className="text-xs text-slate-500">No MRG history available.</p>
        ) : (
          <ListingDataTableCard className="max-w-xl">
            <ListingDataTableScroll>
            <table className={LISTING_DATA_TABLE_CLASS}>
              <thead className={cn(LISTING_DATA_TABLE_HEAD_CLASS, 'text-xs')}>
                <tr>
                  <th className="px-3 py-2 text-left">MRG Percentage</th>
                  <th className="px-3 py-2 text-left">Effective Start Date</th>
                </tr>
              </thead>
              <tbody className={LISTING_DATA_TABLE_BODY_CLASS}>
                {mrgRows.map((row, idx) => (
                  <tr key={`${row.year}-${row.effectiveStartDate}-${idx}`}>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={`${row.percent}%`}
                        readOnly
                        className="w-24 px-2 py-1 text-sm border border-slate-200 rounded bg-slate-50"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={formatMrgDate(row.effectiveStartDate)}
                        readOnly
                        className="w-32 px-2 py-1 text-sm border border-slate-200 rounded bg-slate-50"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ListingDataTableScroll>
          </ListingDataTableCard>
        )}
      </SectionCard>

      <SectionCard number={4} title="Other Program Option">
        <div className="flex flex-wrap gap-8">
          <FieldLegend label="Defaults ACH">
            <SegmentedButtonGroup
              ariaLabel="Default ACH"
              value={watch('DEFAULT_ACH_STATUS') ?? ''}
              onChange={(v) =>
                setValue('DEFAULT_ACH_STATUS', v as OnOff, { shouldDirty: true, shouldValidate: true })
              }
              options={[
                { value: 'ON', label: 'On' },
                { value: 'OFF', label: 'Off' },
              ]}
            />
          </FieldLegend>
          <FieldLegend label="UpTempo Program">
            <SegmentedButtonGroup
              ariaLabel="UpTempo program"
              value={watch('UPTEMPO_PROGRAM_STATUS') ?? ''}
              onChange={(v) =>
                setValue('UPTEMPO_PROGRAM_STATUS', v as OnOff, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              options={[
                { value: 'ON', label: 'On' },
                { value: 'OFF', label: 'Off' },
              ]}
            />
          </FieldLegend>
        </div>
      </SectionCard>

      <SectionCard number={5} title="Payment Window">
        <div className="max-w-xs">
          <LabeledInput
            label="Payment Window (Minutes)"
            {...integerTextInputAttrs}
            {...register('PAYMENT_WINDOW', {
              validate: validatePaymentWindow,
            })}
          />
          {errors.PAYMENT_WINDOW?.message && (
            <p className="mt-1 text-xs text-red-600" role="alert">
              {errors.PAYMENT_WINDOW.message}
            </p>
          )}
        </div>
      </SectionCard>

      <div>
        <button
          type="submit"
          disabled={!isDirty || submitLoading || !isValid}
          className={primaryEntityActionButtonClass}
        >
          {submitLoading ? 'Saving…' : 'Submit'}
        </button>
      </div>
    </form>
  )
}
